import type { Message } from '../types';
import { looksLikePlcCode } from './messageTopic';

type AnyRecord = Record<string, any>;

type PlanReview = NonNullable<Message['planReview']>;

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function asRecord(value: unknown): AnyRecord {
  return value && typeof value === 'object' ? value as AnyRecord : {};
}

function normalizeText(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function getChecklist(message: Message): AnyRecord[] {
  return Array.isArray(message.clarificationChecklist)
    ? message.clarificationChecklist.map(asRecord).filter(item => hasText(item.question) || hasText(item.title) || hasText(item.id))
    : [];
}

function getItemId(item: AnyRecord, index: number): string {
  return String(item.id || item.key || item.fieldKey || item.question || `clarify-${index}`);
}

function hasRawPlanGenerationArtifact(message: Message): boolean {
  return Boolean(
    hasText(message.planGenerationReasoning)
      || hasText(message.planTransitionText)
      || message.stageBuffers?.planGeneration?.started
      || message.stageBuffers?.planReview?.artifact
      || message.planProcessSummary
      || message.planReview
      || message.planProcessHistory?.some(item =>
        Boolean(item.planReview)
          || Boolean(item.summary)
          || (
            item.stage === 'planning'
              && (hasText(item.reasoning) || Boolean(item.summary))
          )
          || (
            (item.status === 'awaiting_review' || item.status === 'submitted' || item.status === 'completed')
              && (hasText(item.reasoning) || Boolean(item.summary))
          ),
      ),
  );
}

function hasRawCodeArtifact(message: Message): boolean {
  return Boolean(message.parsed?.code && looksLikePlcCode(message.parsed.code))
    || Boolean(message.repairAppendix?.finalCode && looksLikePlcCode(message.repairAppendix.finalCode));
}

function hasRawValidationArtifact(message: Message): boolean {
  return Boolean(
    message.pipeline?.validationReport
      || message.parsed?.verification
      || message.repairAppendix?.finalValidation,
  );
}

function hasRawRepairArtifact(message: Message): boolean {
  return Boolean(
    message.repairAppendix?.status === 'repairing'
      || message.repairAppendix?.status === 'completed'
      || message.repairAppendix?.status === 'completed_with_warnings'
      || message.repairAppendix?.status === 'failed'
      || message.pipeline?.repairHistory?.length,
  );
}

function isAutoConfirmedClarificationItem(item: AnyRecord): boolean {
  const status = normalizeText(item.status || item.state || item.reviewStatus || item.confirmationStatus);
  const source = normalizeText(item.source || item.origin || item.confirmationSource);
  return Boolean(
    item.autoConfirmed === true
      || item.autoConfirm === true
      || item.auto === true
      || item.isAuto === true
      || item.generated === true
      || status === 'auto'
      || status === 'auto_confirmed'
      || status === 'autoconfirmed'
      || source === 'auto'
      || source === 'system'
      || source === 'default',
  );
}

function isRequiredClarificationItem(item: AnyRecord): boolean {
  const priority = normalizeText(item.priority || item.level || item.severity || item.importance);
  const status = normalizeText(item.status || item.state);
  const kind = normalizeText(item.kind || item.type || item.category);

  // In the UI contract, clarification cards that are not explicitly optional and
  // not auto-confirmed are treated as human-required by default. Many backend
  // payloads only mark optional/auto cards, so relying only on required === true
  // makes the pipeline jump into engineering/planning too early.
  if (item.required === false || item.isRequired === false || item.optional === true) return false;
  if (isAutoConfirmedClarificationItem(item)) return false;

  if (
    item.required === true
      || item.isRequired === true
      || item.mustConfirm === true
      || item.needsHumanConfirmation === true
      || item.requiresHumanConfirmation === true
      || item.needsInput === true
      || item.pending === true
      || priority === 'required'
      || priority === 'must'
      || priority === 'critical'
      || priority === 'high'
      || status === 'pending'
      || status === 'needs_input'
      || status === 'needs_user_input'
      || status === 'required'
      || kind === 'required'
      || kind === 'manual'
      || kind === 'human'
  ) {
    return true;
  }

  // Default: visible non-auto clarification cards require a user decision.
  return true;
}

function hasAnsweredClarificationItem(item: AnyRecord): boolean {
  const status = normalizeText(item.status || item.state || item.reviewStatus || item.confirmationStatus);
  return Boolean(
    isAutoConfirmedClarificationItem(item)
      || item.answered === true
      || item.resolved === true
      || item.confirmed === true
      || item.submitted === true
      || item.userConfirmed === true
      || hasText(item.answer)
      || hasText(item.userAnswer)
      || hasText(item.value)
      || hasText(item.selectedValue)
      || hasText(item.selectedOption)
      || hasText(item.selectedOptionId)
      || (Array.isArray(item.selectedOptions) && item.selectedOptions.length > 0)
      || (Array.isArray(item.answers) && item.answers.length > 0)
      || status === 'answered'
      || status === 'confirmed'
      || status === 'resolved'
      || status === 'submitted'
      || status === 'complete'
      || status === 'completed',
  );
}

function getRequiredClarificationStats(message: Message) {
  const checklist = getChecklist(message);
  const required = checklist.filter(isRequiredClarificationItem);
  const answered = required.filter(hasAnsweredClarificationItem);
  const pending = required.filter(item => !hasAnsweredClarificationItem(item));

  return {
    checklist,
    required,
    answered,
    pending,
    totalCount: checklist.length,
    requiredCount: required.length,
    answeredRequiredCount: answered.length,
    pendingRequiredCount: pending.length,
    pendingRequiredIds: pending.map(getItemId),
  };
}

export function getLatestPlanReview(message: Message): Message['planReview'] {
  const history = message.planProcessHistory || [];
  for (let index = history.length - 1; index >= 0; index -= 1) {
    if (history[index]?.planReview) return history[index].planReview;
  }
  return message.planReview || message.stageBuffers?.planReview?.artifact as Message['planReview'];
}

function isRequirementAnalysisInProgress(message: Message): boolean {
  const requirementAnalysis = message.stageBuffers?.requirementAnalysis;
  if (requirementAnalysis?.status === 'streaming' && !requirementAnalysis.done) return true;
  if (requirementAnalysis?.done || requirementAnalysis?.status === 'completed') return false;

  const phase = normalizeText(message.progressState?.phase);
  const stage = normalizeText(message.progressState?.stage);
  const planPhase = normalizeText(message.progressState?.planPhase);

  return Boolean(
    message.isStreaming
      && (
        phase === 'queued'
          || phase === 'dispatching'
          || phase === 'classifying'
          || phase === 'clarifying'
          || stage === 'routing'
          || stage === 'requirement_analysis'
          || stage === 'clarification'
          // Text flowing in these phases does not end the analysis: the classifying one streams the question of
          // the cards. Where the stage has buffers, they decided above.
          || (phase === 'planning' && (planPhase === 'analyzing' || planPhase === 'classifying'))
      ),
  );
}

function hasExplicitEngineeringReasoningStage(message: Message): boolean {
  const phase = normalizeText(message.progressState?.phase);
  const stage = normalizeText(message.progressState?.stage);
  const planPhase = normalizeText(message.progressState?.planPhase);

  // Begun when its own stage has, or the turn says so. Not because text has flowed: the classifying phase streams
  // the question of the cards, from a turn that has not got there (the message's `reasoning` was read as if it had).
  return Boolean(
    message.stageBuffers?.planBasis?.started
      || phase === 'engineering_reasoning'
      || stage === 'engineering_reasoning'
      || planPhase === 'engineering_reasoning'
      // Some older runtime chunks use planning/analyzing as the handoff stage
      // before a concrete plan artifact exists. Treat that as engineering
      // reasoning in progress only while there is not yet a plan to display.
      || (
        phase === 'planning'
          && !hasRawPlanGenerationArtifact(message)
          && (!planPhase || planPhase === 'analyzing' || planPhase === 'classifying')
      ),
  );
}

function isDownstreamProgressActive(message: Message): boolean {
  const phase = normalizeText(message.progressState?.phase);
  const stage = normalizeText(message.progressState?.stage);
  return Boolean(
    phase === 'generating'
      || phase === 'validating'
      || phase === 'repairing'
      || phase === 'finalizing'
      || stage === 'generation'
      || stage === 'validation'
      || stage === 'repair',
  );
}

export function getWorkflowStageGates(message: Message) {
  const stats = getRequiredClarificationStats(message);
  const pendingChipCount = message.pendingChipIds?.length || 0;
  const hasLegacyClarifyPending = Boolean(
    message.parsed?.clarify
      && stats.totalCount === 0
      && pendingChipCount === 0
      && !hasRawPlanGenerationArtifact(message)
      && !hasRawCodeArtifact(message)
      && !hasRawValidationArtifact(message)
      && !hasRawRepairArtifact(message),
  );

  const hasHumanConfirmation = Boolean(
    stats.totalCount > 0
      || pendingChipCount > 0
      || message.parsed?.clarify,
  );

  const pendingHumanConfirmationCount = stats.pendingRequiredCount + pendingChipCount + (hasLegacyClarifyPending ? 1 : 0);

  // Important: “no confirmation items have arrived yet” is NOT the same as
  // “confirmation has completed”. During streaming requirement analysis, the
  // checklist may still be empty, so the old `!hasHumanConfirmation` shortcut
  // made the UI jump into engineering reasoning too early.
  const requirementAnalysisInProgress = isRequirementAnalysisInProgress(message);
  const planReview = getLatestPlanReview(message) as PlanReview | undefined;
  const rawDownstreamArtifact = hasRawDownstreamArtifact(message);
  const downstreamProgressActive = isDownstreamProgressActive(message);
  const committedPlanArtifact = Boolean(hasRawPlanGenerationArtifact(message) || planReview);
  const planPhase = normalizeText(message.progressState?.planPhase);
  const planReviewMetadataPending = Boolean(
    message.isStreaming
      && planPhase === 'done'
      && hasRawPlanGenerationArtifact(message)
      && !planReview,
  );
  const downstreamArtifactCanAdvance = rawDownstreamArtifact
    && (!message.isStreaming || downstreamProgressActive || Boolean(planReview) || planReviewMetadataPending);

  // `downstreamArtifactCanAdvance` counts on both branches. A turn that reached
  // code, validation or repair has moved past confirmation whether or not anyone
  // answered the checklist — and a repair follow-up never produces the plan
  // artifact this used to demand as the only proof, which is what pinned every
  // downstream row of its timeline at 等待中 forever. The guard against artifacts
  // proving too much lives inside `downstreamArtifactCanAdvance`, which will not
  // fire mid-stream unless the run is demonstrably past the gate.
  const humanConfirmationResolved = hasHumanConfirmation
    ? pendingHumanConfirmationCount === 0 || committedPlanArtifact || downstreamArtifactCanAdvance
    : !requirementAnalysisInProgress && Boolean(
      hasExplicitEngineeringReasoningStage(message)
        || downstreamArtifactCanAdvance,
    );

  const explicitEngineeringReasoningStage = hasExplicitEngineeringReasoningStage(message);

  const planApprovalResolvedOrNotRequired = !planReview
    || planReview.requiresApproval === false
    || planReview.reviewResult === 'approved'
    || planReview.reviewResult === 'modified';

  const canEnterEngineeringReasoning = Boolean(
    humanConfirmationResolved
      && !requirementAnalysisInProgress,
  );

  const hasEngineeringReasoningArtifact = Boolean(
    canEnterEngineeringReasoning
      && (
        explicitEngineeringReasoningStage
          // Compatibility for historical completed messages that predate the
          // explicit engineering_reasoning artifact. This is deliberately
          // disabled while streaming so artifacts cannot prove reasoning too early.
          || downstreamArtifactCanAdvance
      ),
  );

  const canEnterPlanGeneration = Boolean(
    canEnterEngineeringReasoning
      && hasEngineeringReasoningArtifact,
  );

  return {
    hasHumanConfirmation,
    humanConfirmationResolved,
    hasPendingHumanConfirmation: pendingHumanConfirmationCount > 0,
    pendingHumanConfirmationCount,
    requiredHumanConfirmationCount: stats.requiredCount + (hasLegacyClarifyPending ? 1 : 0),
    answeredRequiredHumanConfirmationCount: stats.answeredRequiredCount,
    pendingRequiredHumanConfirmationIds: stats.pendingRequiredIds,
    requirementAnalysisInProgress,
    explicitEngineeringReasoningStage,
    hasEngineeringReasoningArtifact,
    hasPlanReview: Boolean(planReview),
    planReview,
    planApprovalResolvedOrNotRequired,
    canEnterEngineeringReasoning,
    canEnterPlanGeneration,
    canEnterPlanReview: canEnterPlanGeneration,
    canShowCode: canEnterPlanGeneration && planApprovalResolvedOrNotRequired,
    canShowValidation: canEnterPlanGeneration && planApprovalResolvedOrNotRequired,
    canShowRepair: canEnterPlanGeneration && planApprovalResolvedOrNotRequired,
  };
}

export function hasRawDownstreamArtifact(message: Message): boolean {
  return Boolean(
    hasRawPlanGenerationArtifact(message)
      || hasRawCodeArtifact(message)
      || hasRawValidationArtifact(message)
      || hasRawRepairArtifact(message),
  );
}
